/**
 * reCAPTCHA v3 helper.
 *
 * Wraps @nuxt/scripts' `useScriptGoogleRecaptcha` (the same registry-script
 * mechanism used for GA4 / Meta Pixel) to load reCAPTCHA v3 and mint a fresh
 * token on demand. The token is sent with form submissions and verified
 * server-side by the CMS (`/v1/contact-us/`).
 *
 * Configure the site key under `environment.thirdPartyApi.google.recaptcha.key`
 * in the per-site config (see `config/sites/<site>/config*.mjs`). When no key is
 * configured the helper is disabled and `execute()` resolves to `null`, so the
 * form keeps working exactly as before and the backend can decide how to treat
 * a missing token.
 */
export function useRecaptcha(defaultAction = 'submit') {
    const $config = useRuntimeConfig();
    const google = ($config.public.environment?.thirdPartyApi?.google ?? {}) as Record<string, any>;
    const siteKey: string = google?.recaptcha?.key ?? '';

    // `useScriptGoogleRecaptcha` is auto-imported by @nuxt/scripts; guard with
    // `typeof` in case the module isn't registered for a given site build.
    const isEnabled = !!siteKey && typeof useScriptGoogleRecaptcha === 'function';

    // Register/load the script during setup so reCAPTCHA can observe page
    // interactions before `execute()` runs — this improves v3 scoring.
    const script = isEnabled
        ? useScriptGoogleRecaptcha({
            siteKey,
            scriptOptions: { trigger: 'onNuxtReady' }
        })
        : null;

    /**
     * Request a fresh reCAPTCHA v3 token for the given action. Resolves to
     * `null` when reCAPTCHA is disabled or fails to load so submission is never
     * blocked indefinitely.
     */
    async function execute(action: string = defaultAction): Promise<Nullable<string>> {
        if (!script) {
            return null;
        }

        return new Promise<Nullable<string>>((resolve) => {
            let settled = false;
            const finish = (token: Nullable<string>) => {
                if (settled) return;
                settled = true;
                resolve(token);
            };

            script.onLoaded(({ grecaptcha }) => {
                grecaptcha.ready(() => {
                    grecaptcha
                        .execute(siteKey, { action })
                        .then((token: string) => finish(token))
                        .catch(() => finish(null));
                });
            });

            script.onError?.(() => finish(null));

            // Safety net: don't hang submission if the script is blocked/slow.
            setTimeout(() => finish(null), 8000);
        });
    }

    return {
        isEnabled,
        execute
    };
}
